// Tutorial5.jsx - Stoichiometry & Equilibrium Conversion (Updated with Diagrams)
import React from 'react';
import Navbar from '../../Navbar';
import Sidebar from '../../Sidebar';
import { BlockMath } from 'react-katex';
import '../../../styles/Tutorial5.css';

// ============================================================
// SVG DIAGRAM COMPONENTS
// ============================================================

// IMAGE 1: Batch Reactor Diagram (Page 3)
const BatchReactorDiagram = () => (
  <svg viewBox="0 0 200 200" width="180" height="180">
    <ellipse cx="100" cy="40" rx="50" ry="15" fill="none" stroke="#1b5e3f" strokeWidth="2"/>
    <rect x="50" y="40" width="100" height="100" rx="5" fill="rgba(27, 94, 63, 0.08)" stroke="#1b5e3f" strokeWidth="2"/>
    <ellipse cx="100" cy="140" rx="50" ry="15" fill="none" stroke="#1b5e3f" strokeWidth="2"/>
    <rect x="52" y="80" width="96" height="58" fill="rgba(27, 94, 63, 0.15)" rx="3"/>
    <text x="100" y="110" textAnchor="middle" fontSize="11" fill="#1b5e3f" fontFamily="monospace">Liquid</text>
    <line x1="100" y1="25" x2="100" y2="140" stroke="#1b5e3f" strokeWidth="1.5"/>
    <ellipse cx="100" cy="35" rx="20" ry="5" fill="none" stroke="#1b5e3f" strokeWidth="1.5"/>
    <line x1="85" y1="120" x2="115" y2="120" stroke="#1b5e3f" strokeWidth="2"/>
    <line x1="90" y1="130" x2="110" y2="130" stroke="#1b5e3f" strokeWidth="2"/>
    <rect x="48" y="42" width="104" height="96" rx="5" fill="none" stroke="#d89f3a" strokeWidth="1.5" strokeDasharray="4,3"/>
    <text x="100" y="175" textAnchor="middle" fontSize="10" fill="#5a7360" fontFamily="monospace">Batch Reactor</text>
    <text x="100" y="190" textAnchor="middle" fontSize="9" fill="#7a8e80" fontFamily="monospace">V, T, P</text>
  </svg>
);

// IMAGE 2: Rate Law + Stoichiometry Flow Diagram (Page 4)
const RateLawStoichFlowDiagram = () => (
  <svg viewBox="0 0 500 180" width="100%" maxWidth="500" height="auto">
    <rect x="20" y="40" width="130" height="100" rx="8" fill="rgba(58, 139, 150, 0.12)" stroke="#3a8b96" strokeWidth="2"/>
    <text x="85" y="65" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#3a8b96" fontFamily="monospace">Step 1</text>
    <text x="85" y="85" textAnchor="middle" fontSize="10" fill="#2a3f33" fontFamily="monospace">Rate Law</text>
    <text x="85" y="105" textAnchor="middle" fontSize="11" fill="#1b5e3f" fontFamily="monospace">-r_A = g(C_i)</text>
    <line x1="155" y1="90" x2="200" y2="90" stroke="#5a7360" strokeWidth="2" markerEnd="url(#arrow1)"/>
    <defs>
      <marker id="arrow1" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#5a7360"/>
      </marker>
    </defs>
    <rect x="205" y="40" width="130" height="100" rx="8" fill="rgba(44, 107, 138, 0.10)" stroke="#2c6b8a" strokeWidth="2"/>
    <text x="270" y="65" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#2c6b8a" fontFamily="monospace">Step 2</text>
    <text x="270" y="85" textAnchor="middle" fontSize="10" fill="#2a3f33" fontFamily="monospace">Stoichiometry</text>
    <text x="270" y="105" textAnchor="middle" fontSize="11" fill="#1b5e3f" fontFamily="monospace">C_i = h(X)</text>
    <line x1="340" y1="90" x2="385" y2="90" stroke="#5a7360" strokeWidth="2" markerEnd="url(#arrow2)"/>
    <defs>
      <marker id="arrow2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#5a7360"/>
      </marker>
    </defs>
    <rect x="390" y="40" width="100" height="100" rx="8" fill="rgba(27, 94, 63, 0.10)" stroke="#1b5e3f" strokeWidth="2"/>
    <text x="440" y="65" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#1b5e3f" fontFamily="monospace">Step 3</text>
    <text x="440" y="85" textAnchor="middle" fontSize="10" fill="#2a3f33" fontFamily="monospace">Combine</text>
    <text x="440" y="105" textAnchor="middle" fontSize="11" fill="#1b5e3f" fontFamily="monospace">-r_A = f(X)</text>
    <text x="85" y="165" textAnchor="middle" fontSize="9" fill="#7a8e80" fontFamily="monospace">Rate as function</text>
    <text x="85" y="178" textAnchor="middle" fontSize="9" fill="#7a8e80" fontFamily="monospace">of concentrations</text>
    <text x="270" y="165" textAnchor="middle" fontSize="9" fill="#7a8e80" fontFamily="monospace">Concentrations as</text>
    <text x="270" y="178" textAnchor="middle" fontSize="9" fill="#7a8e80" fontFamily="monospace">function of conversion</text>
    <text x="440" y="165" textAnchor="middle" fontSize="9" fill="#7a8e80" fontFamily="monospace">Rate as function</text>
    <text x="440" y="178" textAnchor="middle" fontSize="9" fill="#7a8e80" fontFamily="monospace">of conversion only</text>
  </svg>
);

// IMAGE 3: Three Building Blocks Diagram (Page 5)
const ThreeBuildingBlocksDiagram = () => (
  <svg viewBox="0 0 600 400" width="100%" maxWidth="600" height="auto">
    <text x="300" y="30" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#0f2d1f" fontFamily="monospace">The Three Building Blocks of CRE</text>
    <rect x="200" y="50" width="200" height="70" rx="10" fill="rgba(27, 94, 63, 0.12)" stroke="#1b5e3f" strokeWidth="2.5"/>
    <text x="300" y="78" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1b5e3f" fontFamily="monospace">BLOCK 3</text>
    <text x="300" y="98" textAnchor="middle" fontSize="13" fill="#0f2d1f" fontFamily="sans-serif">Stoichiometry</text>
    <text x="300" y="112" textAnchor="middle" fontSize="9" fill="#5a7360" fontFamily="monospace">Cᵢ = h(X)</text>
    <line x1="300" y1="120" x2="300" y2="155" stroke="#5a7360" strokeWidth="2" markerEnd="url(#arrow3)"/>
    <defs>
      <marker id="arrow3" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#5a7360"/>
      </marker>
    </defs>
    <rect x="175" y="160" width="250" height="70" rx="10" fill="rgba(44, 107, 138, 0.10)" stroke="#2c6b8a" strokeWidth="2.5"/>
    <text x="300" y="188" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#2c6b8a" fontFamily="monospace">BLOCK 2</text>
    <text x="300" y="208" textAnchor="middle" fontSize="13" fill="#0f2d1f" fontFamily="sans-serif">Rate Laws</text>
    <text x="300" y="222" textAnchor="middle" fontSize="9" fill="#5a7360" fontFamily="monospace">-r_A = g(Cᵢ)</text>
    <line x1="300" y1="230" x2="300" y2="265" stroke="#5a7360" strokeWidth="2" markerEnd="url(#arrow4)"/>
    <defs>
      <marker id="arrow4" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#5a7360"/>
      </marker>
    </defs>
    <rect x="150" y="270" width="300" height="70" rx="10" fill="rgba(216, 159, 58, 0.10)" stroke="#d89f3a" strokeWidth="2.5"/>
    <text x="300" y="298" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#d89f3a" fontFamily="monospace">BLOCK 1</text>
    <text x="300" y="318" textAnchor="middle" fontSize="13" fill="#0f2d1f" fontFamily="sans-serif">Mole Balances</text>
    <text x="300" y="332" textAnchor="middle" fontSize="9" fill="#5a7360" fontFamily="monospace">Design Equations in terms of X</text>
    <text x="80" y="100" fontSize="9" fill="#7a8e80" fontFamily="monospace">Concentrations</text>
    <text x="80" y="115" fontSize="9" fill="#7a8e80" fontFamily="monospace">as function</text>
    <text x="80" y="130" fontSize="9" fill="#7a8e80" fontFamily="monospace">of X</text>
    <text x="80" y="210" fontSize="9" fill="#7a8e80" fontFamily="monospace">Rate as function</text>
    <text x="80" y="225" fontSize="9" fill="#7a8e80" fontFamily="monospace">of concentrations</text>
    <text x="520" y="115" fontSize="9" fill="#7a8e80" fontFamily="monospace">Bridge between</text>
    <text x="520" y="130" fontSize="9" fill="#7a8e80" fontFamily="monospace">Rate Law and</text>
    <text x="520" y="145" fontSize="9" fill="#7a8e80" fontFamily="monospace">Mole Balance</text>
    <text x="300" y="370" textAnchor="middle" fontSize="10" fill="#d89f3a" fontFamily="monospace">↓ These topics build upon one another ↓</text>
  </svg>
);

// IMAGE 4: Flow System Stoichiometric Table (Page 6)
const FlowSystemStoichTable = () => (
  <svg viewBox="0 0 800 350" width="100%" maxWidth="800" height="auto">
    <rect x="20" y="20" width="180" height="35" rx="5" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="110" y="43" textAnchor="middle" fontSize="13" fontWeight="bold" fill="white" fontFamily="monospace">Species</text>
    <rect x="200" y="20" width="180" height="35" rx="5" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="290" y="43" textAnchor="middle" fontSize="13" fontWeight="bold" fill="white" fontFamily="monospace">Feed Rate</text>
    <rect x="380" y="20" width="180" height="35" rx="5" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="470" y="43" textAnchor="middle" fontSize="13" fontWeight="bold" fill="white" fontFamily="monospace">Change</text>
    <rect x="560" y="20" width="220" height="35" rx="5" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="670" y="43" textAnchor="middle" fontSize="13" fontWeight="bold" fill="white" fontFamily="monospace">Outlet Rate</text>
    <rect x="20" y="55" width="180" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="110" y="82" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#1b5e3f" fontFamily="monospace">A</text>
    <rect x="200" y="55" width="180" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="290" y="82" textAnchor="middle" fontSize="12" fill="#2a3f33" fontFamily="monospace">Fₐ₀</text>
    <rect x="380" y="55" width="180" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="470" y="72" textAnchor="middle" fontSize="11" fill="#d89f3a" fontFamily="monospace">-Fₐ₀X</text>
    <rect x="560" y="55" width="220" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="670" y="72" textAnchor="middle" fontSize="12" fill="#1b5e3f" fontFamily="monospace">Fₐ = Fₐ₀(1-X)</text>
    <rect x="20" y="100" width="180" height="45" fill="rgba(44, 107, 138, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="110" y="127" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#2c6b8a" fontFamily="monospace">B</text>
    <rect x="200" y="100" width="180" height="45" fill="rgba(44, 107, 138, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="290" y="127" textAnchor="middle" fontSize="11" fill="#2a3f33" fontFamily="monospace">Fᵦ₀ = ΘᵦFₐ₀</text>
    <rect x="380" y="100" width="180" height="45" fill="rgba(44, 107, 138, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="470" y="117" textAnchor="middle" fontSize="11" fill="#d89f3a" fontFamily="monospace">-(b/a)Fₐ₀X</text>
    <rect x="560" y="100" width="220" height="45" fill="rgba(44, 107, 138, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="670" y="117" textAnchor="middle" fontSize="11" fill="#2c6b8a" fontFamily="monospace">Fᵦ = Fₐ₀(Θᵦ - (b/a)X)</text>
    <rect x="20" y="145" width="180" height="45" fill="rgba(58, 139, 150, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="110" y="172" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#3a8b96" fontFamily="monospace">C</text>
    <rect x="200" y="145" width="180" height="45" fill="rgba(58, 139, 150, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="290" y="172" textAnchor="middle" fontSize="11" fill="#2a3f33" fontFamily="monospace">F꜀₀ = Θ꜀Fₐ₀</text>
    <rect x="380" y="145" width="180" height="45" fill="rgba(58, 139, 150, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="470" y="162" textAnchor="middle" fontSize="11" fill="#d89f3a" fontFamily="monospace">+(c/a)Fₐ₀X</text>
    <rect x="560" y="145" width="220" height="45" fill="rgba(58, 139, 150, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="670" y="162" textAnchor="middle" fontSize="11" fill="#3a8b96" fontFamily="monospace">F꜀ = Fₐ₀(Θ꜀ + (c/a)X)</text>
    <rect x="20" y="190" width="180" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="110" y="217" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#1b5e3f" fontFamily="monospace">D</text>
    <rect x="200" y="190" width="180" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="290" y="217" textAnchor="middle" fontSize="11" fill="#2a3f33" fontFamily="monospace">Fᴅ₀ = ΘᴅFₐ₀</text>
    <rect x="380" y="190" width="180" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="470" y="207" textAnchor="middle" fontSize="11" fill="#d89f3a" fontFamily="monospace">+(d/a)Fₐ₀X</text>
    <rect x="560" y="190" width="220" height="45" fill="rgba(27, 94, 63, 0.05)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="670" y="207" textAnchor="middle" fontSize="11" fill="#1b5e3f" fontFamily="monospace">Fᴅ = Fₐ₀(Θᴅ + (d/a)X)</text>
    <rect x="20" y="235" width="180" height="45" fill="rgba(216, 159, 58, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="110" y="262" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#d89f3a" fontFamily="monospace">Inert (I)</text>
    <rect x="200" y="235" width="180" height="45" fill="rgba(216, 159, 58, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="290" y="262" textAnchor="middle" fontSize="11" fill="#2a3f33" fontFamily="monospace">Fɪ₀ = ΘɪFₐ₀</text>
    <rect x="380" y="235" width="180" height="45" fill="rgba(216, 159, 58, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="470" y="262" textAnchor="middle" fontSize="11" fill="#5a7360" fontFamily="monospace">0</text>
    <rect x="560" y="235" width="220" height="45" fill="rgba(216, 159, 58, 0.04)" stroke="#c5d5ce" strokeWidth="1"/>
    <text x="670" y="262" textAnchor="middle" fontSize="11" fill="#d89f3a" fontFamily="monospace">Fɪ = Fₐ₀Θɪ</text>
    <rect x="20" y="280" width="180" height="45" rx="5" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="110" y="307" textAnchor="middle" fontSize="13" fontWeight="bold" fill="white" fontFamily="monospace">Total</text>
    <rect x="200" y="280" width="180" height="45" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="290" y="307" textAnchor="middle" fontSize="12" fontWeight="bold" fill="white" fontFamily="monospace">Fʀ₀</text>
    <rect x="380" y="280" width="180" height="45" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="470" y="300" textAnchor="middle" fontSize="11" fontWeight="bold" fill="white" fontFamily="monospace">δFₐ₀X</text>
    <rect x="560" y="280" width="220" height="45" rx="5" fill="#1b5e3f" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="670" y="300" textAnchor="middle" fontSize="11" fontWeight="bold" fill="white" fontFamily="monospace">Fʀ = Fʀ₀ + δFₐ₀X</text>
    <text x="400" y="340" textAnchor="middle" fontSize="10" fill="#7a8e80" fontFamily="monospace">where Θᵢ = Fᵢ₀/Fₐ₀  and  δ = d/a + c/a - b/a - 1</text>
  </svg>
);

// IMAGE 5: Equilibrium Conversion Comparison Diagram (Pages 15-20)
const EquilibriumConversionDiagram = () => (
  <svg viewBox="0 0 600 280" width="100%" maxWidth="600" height="auto">
    <text x="300" y="25" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#0f2d1f" fontFamily="monospace">Equilibrium Conversion: Batch vs Flow Reactor</text>
    <text x="300" y="45" textAnchor="middle" fontSize="11" fill="#5a7360" fontFamily="monospace">Reaction: 2A ⇌ B  |  Kc = 20 dm³/mol  |  Cₐ₀ = 0.2 mol/dm³</text>
    <rect x="30" y="65" width="250" height="200" rx="10" fill="rgba(216, 159, 58, 0.06)" stroke="#d89f3a" strokeWidth="2"/>
    <text x="155" y="90" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#d89f3a" fontFamily="monospace">Batch Reactor</text>
    <rect x="105" y="105" width="100" height="60" rx="5" fill="rgba(216, 159, 58, 0.12)" stroke="#d89f3a" strokeWidth="1.5"/>
    <text x="155" y="135" textAnchor="middle" fontSize="9" fill="#d89f3a" fontFamily="monospace">Constant Volume</text>
    <text x="155" y="150" textAnchor="middle" fontSize="9" fill="#d89f3a" fontFamily="monospace">V = V₀</text>
    <text x="155" y="190" textAnchor="middle" fontSize="11" fill="#0f2d1f" fontFamily="monospace">Xₑ = 0.703</text>
    <text x="155" y="208" textAnchor="middle" fontSize="11" fill="#1b5e3f" fontFamily="monospace">(70.3%)</text>
    <line x1="285" y1="165" x2="315" y2="165" stroke="#5a7360" strokeWidth="2.5" markerEnd="url(#arrowEq)"/>
    <text x="300" y="155" textAnchor="middle" fontSize="9" fill="#5a7360" fontFamily="monospace">vs</text>
    <defs>
      <marker id="arrowEq" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#5a7360"/>
      </marker>
    </defs>
    <rect x="320" y="65" width="250" height="200" rx="10" fill="rgba(27, 94, 63, 0.06)" stroke="#1b5e3f" strokeWidth="2"/>
    <text x="445" y="90" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1b5e3f" fontFamily="monospace">Flow Reactor</text>
    <rect x="395" y="105" width="100" height="60" rx="30" fill="rgba(27, 94, 63, 0.12)" stroke="#1b5e3f" strokeWidth="1.5"/>
    <text x="445" y="135" textAnchor="middle" fontSize="9" fill="#1b5e3f" fontFamily="monospace">Variable Volume</text>
    <text x="445" y="150" textAnchor="middle" fontSize="9" fill="#1b5e3f" fontFamily="monospace">ε = -0.5</text>
    <text x="445" y="190" textAnchor="middle" fontSize="11" fill="#0f2d1f" fontFamily="monospace">Xₑ = 0.757</text>
    <text x="445" y="208" textAnchor="middle" fontSize="11" fill="#1b5e3f" fontFamily="monospace">(75.7%)</text>
    <text x="300" y="255" textAnchor="middle" fontSize="10" fill="#7a8e80" fontFamily="monospace">Flow reactor achieves higher conversion due to volume change effects</text>
    <text x="300" y="272" textAnchor="middle" fontSize="9" fill="#5a7360" fontFamily="monospace">(ε = yₐ₀δ = 1 × (½ - 1) = -½)</text>
  </svg>
);

// Reactor Icons Component
const ReactorIcons = () => (
  <div className="cre5-reactors-diagram">
    <div className="cre5-reactor-icon cre5-reactor-icon--batch">
      <svg viewBox="0 0 100 80" width="80" height="64">
        <rect x="10" y="10" width="80" height="60" rx="5" fill="rgba(216, 159, 58, 0.15)" stroke="#d89f3a" strokeWidth="1.5"/>
        <text x="50" y="45" textAnchor="middle" fontSize="9" fill="#d89f3a" fontFamily="monospace">Batch</text>
      </svg>
    </div>
    <div className="cre5-reactor-icon cre5-reactor-icon--cstr">
      <svg viewBox="0 0 100 80" width="80" height="64">
        <rect x="10" y="10" width="80" height="60" rx="5" fill="rgba(44, 107, 138, 0.15)" stroke="#2c6b8a" strokeWidth="1.5"/>
        <text x="50" y="45" textAnchor="middle" fontSize="9" fill="#2c6b8a" fontFamily="monospace">CSTR</text>
      </svg>
    </div>
    <div className="cre5-reactor-icon cre5-reactor-icon--pfr">
      <svg viewBox="0 0 100 80" width="80" height="64">
        <rect x="10" y="10" width="80" height="60" rx="30" fill="rgba(27, 94, 63, 0.15)" stroke="#1b5e3f" strokeWidth="1.5"/>
        <text x="50" y="45" textAnchor="middle" fontSize="9" fill="#1b5e3f" fontFamily="monospace">PFR</text>
      </svg>
    </div>
    <div className="cre5-reactor-icon cre5-reactor-icon--pbr">
      <svg viewBox="0 0 100 80" width="80" height="64">
        <rect x="10" y="10" width="80" height="60" rx="5" fill="rgba(58, 139, 150, 0.15)" stroke="#3a8b96" strokeWidth="1.5"/>
        <text x="50" y="45" textAnchor="middle" fontSize="9" fill="#3a8b96" fontFamily="monospace">PBR</text>
      </svg>
    </div>
  </div>
);

// Main Component
const ChemicalReactionEngineeringNotes5 = () => {
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>

      <Sidebar />

      <div style={{
        marginLeft:    '220px',
        width:         'calc(100% - 220px)',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        <Navbar activePage="app" />

        <div style={{
          flex:      1,
          marginTop: '68px',
          overflowY: 'auto',
          overflowX:  'hidden',
          minHeight: 0,
        }}>
    <div className="cre5-container" style={{ background: 'var(--t5-bg)', minHeight: '100vh' }}>

      {/* SIDEBAR */}
      <nav className="cre5-sidebar">
        <div className="cre5-sidebar-header">
          <h1>CRE Course</h1>
          <p>Prof. Fogler · Lecture 5</p>
        </div>

        <div className="cre5-nav-section">Building Blocks</div>
        <a className="cre5-nav-item" href="#three-blocks">Three Building Blocks</a>
        <a className="cre5-nav-item" href="#mole-balances">Mole Balances</a>
        <a className="cre5-nav-item" href="#rate-laws">Rate Laws</a>
        <a className="cre5-nav-item" href="#stoichiometry">Stoichiometry</a>

        <div className="cre5-nav-section">Stoichiometric Tables</div>
        <a className="cre5-nav-item" href="#flow-system">Flow System</a>
        <a className="cre5-nav-item cre5-nav-item--sub" href="#flow-definitions">Definitions of Concentration</a>
        <a className="cre5-nav-item cre5-nav-item--sub" href="#gas-phase-flow">Gas Phase Flow Rate</a>
        <a className="cre5-nav-item" href="#stoich-table-flow">Stoichiometric Table: Flow</a>

        <div className="cre5-nav-section">Equilibrium Conversion</div>
        <a className="cre5-nav-item" href="#equilibrium-concept">Equilibrium Concepts</a>
        <a className="cre5-nav-item" href="#calculate-xe">Calculate Xₑ</a>
        <a className="cre5-nav-item cre5-nav-item--sub" href="#flow-reactor">Flow Reactor Example</a>
        <a className="cre5-nav-item cre5-nav-item--sub" href="#batch-reactor">Batch Reactor Example</a>

        <div className="cre5-nav-section">Reference</div>
        <a className="cre5-nav-item" href="#key-symbols">Key Symbols</a>
        <a className="cre5-nav-item" href="#reactor-summary">Reactor Summary</a>
      </nav>

      {/* MAIN CONTENT */}
      <main className="cre5-main">
        <div className="cre5-main-inner">

          {/* HERO */}
          <div className="cre5-hero">
            <div className="cre5-hero-tag">Open Course Notes · Chapter 4</div>
            <h1>CRE Lecture 5: Stoichiometry &amp; Equilibrium Conversion</h1>
            <p className="cre5-hero-desc">
              Building upon the three fundamental blocks of Reaction Engineering — 
              <strong>Mole Balances</strong>, <strong>Rate Laws</strong>, and <strong>Stoichiometry</strong> — 
              this lecture covers stoichiometric tables for flow and batch systems, gas phase considerations,
              and the calculation of equilibrium conversion (X<sub>e</sub>) for both batch and flow reactors.
            </p>
            <div className="cre5-hero-meta">
              <span className="cre5-meta-item"><span className="cre5-meta-dot"></span>Fogler's Elements of CRE, 6th Ed.</span>
              <span className="cre5-meta-item"><span className="cre5-meta-dot"></span>Lecture 5 · Chapter 4</span>
              <span className="cre5-meta-item"><span className="cre5-meta-dot"></span>University of Michigan</span>
            </div>
          </div>

          {/* TOC */}
          <div className="cre5-toc-box">
            <h3>Quick Navigation</h3>
            <div className="cre5-toc-grid">
              <a className="cre5-toc-link" href="#three-blocks">Three Building Blocks</a>
              <a className="cre5-toc-link" href="#mole-balances">Mole Balances</a>
              <a className="cre5-toc-link" href="#rate-laws">Rate Laws</a>
              <a className="cre5-toc-link" href="#stoichiometry">Stoichiometry</a>
              <a className="cre5-toc-link" href="#flow-system">Flow System</a>
              <a className="cre5-toc-link" href="#flow-definitions">Definitions</a>
              <a className="cre5-toc-link" href="#gas-phase-flow">Gas Phase</a>
              <a className="cre5-toc-link" href="#stoich-table-flow">Stoich Table</a>
              <a className="cre5-toc-link" href="#equilibrium-concept">Equilibrium</a>
              <a className="cre5-toc-link" href="#calculate-xe">Calculate Xₑ</a>
              <a className="cre5-toc-link" href="#key-symbols">Key Symbols</a>
            </div>
          </div>

          {/* DIAGRAM 1: Three Building Blocks Visual */}
          <section className="cre5-section" id="three-blocks">
            <div className="cre5-lec-badge cre5-lec-badge--foundation">Foundation</div>
            <h2>The Three Building Blocks of CRE</h2>
            <div className="cre5-section-divider"></div>
            
            <div className="cre5-diagram-container">
              <ThreeBuildingBlocksDiagram />
            </div>

            <div className="cre5-callout cre5-callout--key">
              <p><strong>Key Insight:</strong> These three building blocks build upon one another to solve any CRE problem. The stoichiometry step is the critical bridge between the rate law and the mole balance.</p>
            </div>
          </section>

          {/* MOLE BALANCES REVIEW */}
          <section className="cre5-section" id="mole-balances">
            <div className="cre5-lec-badge cre5-lec-badge--block1">Block 1 · Mole Balances Review</div>
            <h2>Reactor Mole Balances Summary in terms of X</h2>
            <div className="cre5-section-divider"></div>

            {/* Reactor Icons */}
            <ReactorIcons />

            <div className="cre5-reactor-table">
              <table className="cre5-table">
                <thead>
                  <tr>
                    <th>Reactor</th>
                    <th>Differential Form</th>
                    <th>Algebraic Form</th>
                    <th>Integral Form</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Batch</strong></td>
                    <td><BlockMath math="N_{A0}\frac{dX}{dt} = -r_A V" /></td>
                    <td>—</td>
                    <td><BlockMath math="t = N_{A0}\int_0^X \frac{dX}{-r_A V}" /></td>
                  </tr>
                  <tr>
                    <td><strong>CSTR</strong></td>
                    <td>—</td>
                    <td><BlockMath math="V = \frac{F_{A0}X}{-r_A}" /></td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td><strong>PFR</strong></td>
                    <td><BlockMath math="F_{A0}\frac{dX}{dV} = -r_A" /></td>
                    <td>—</td>
                    <td><BlockMath math="V = F_{A0}\int_0^X \frac{dX}{-r_A}" /></td>
                  </tr>
                  <tr>
                    <td><strong>PBR</strong></td>
                    <td><BlockMath math="F_{A0}\frac{dX}{dW} = -r_A^{\prime}" /></td>
                    <td>—</td>
                    <td><BlockMath math="W = F_{A0}\int_0^X \frac{dX}{-r_A^{\prime}}" /></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="cre5-diagram-container">
              <BatchReactorDiagram />
            </div>

            <div className="cre5-callout cre5-callout--info">
              <p><strong>Note:</strong> All design equations are expressed in terms of conversion X, making it the central design variable. The PFR and batch equations integrate 1/(-r<sub>A</sub>) over X, while CSTR is algebraic.</p>
            </div>
          </section>

          {/* RATE LAWS REVIEW */}
          <section className="cre5-section" id="rate-laws">
            <div className="cre5-lec-badge cre5-lec-badge--block2">Block 2 · Rate Laws Review</div>
            <h2>How to find −r<sub>A</sub> = f(X)</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-diagram-container">
              <RateLawStoichFlowDiagram />
            </div>

            <div className="cre5-callout cre5-callout--rate">
              <div style={{ marginBottom: '15px' }}>
                <strong>Power Law Model:</strong>
              </div>
              <BlockMath math="-r_A = k C_A^{\alpha} C_B^{\beta}" />
            </div>

            {/* Arrhenius Plot */}
            <div className="cre5-arrhenius-container">
              <div className="cre5-arrhenius-title">Arrhenius Equation — ln(k) vs 1/T</div>
              <svg viewBox="0 0 500 300" className="cre5-arrhenius-svg" aria-label="Arrhenius plot showing ln(k) vs 1/T">
                <line x1="60" y1="260" x2="450" y2="260" stroke="#1a2e1a" strokeWidth="2"/>
                <line x1="60" y1="260" x2="60" y2="30" stroke="#1a2e1a" strokeWidth="2"/>
                <polygon points="450,257 450,263 458,260" fill="#1a2e1a"/>
                <polygon points="57,30 63,30 60,22" fill="#1a2e1a"/>
                <text x="458" y="264" fontSize="12" fill="#1a2e1a" fontFamily="monospace">1/T (K⁻¹)</text>
                <text x="20" y="150" fontSize="12" fill="#1a2e1a" fontFamily="monospace" transform="rotate(-90,20,150)" textAnchor="middle">ln(k)</text>
                <line x1="80" y1="240" x2="400" y2="60" stroke="#1b5e3f" strokeWidth="2.5"/>
                <text x="300" y="180" fontSize="11" fill="#1b5e3f" fontFamily="monospace">slope = -E/R</text>
                <line x1="60" y1="260" x2="60" y2="266" stroke="#1a2e1a" strokeWidth="1.5"/>
                <text x="60" y="278" fontSize="10" fill="#5a6e58" textAnchor="middle" fontFamily="monospace">0</text>
              </svg>
              <div className="cre5-arrhenius-caption">
                <BlockMath math="k = A e^{-E/RT}" /> — The Arrhenius equation shows that ln(k) is linear with 1/T.
              </div>
            </div>
          </section>

          {/* STOICHIOMETRY SECTION */}
          <section className="cre5-section" id="stoichiometry">
            <div className="cre5-lec-badge cre5-lec-badge--block3">Block 3 · Stoichiometry</div>
            <h2>Stoichiometric Relationships</h2>
            <div className="cre5-section-divider"></div>

            <p>
              For the general reaction:
            </p>
            <div className="cre5-equation-box cre5-equation-box--primary">
              <div className="cre5-eq-label">General Reaction</div>
              <BlockMath math="\mathrm{A} + \frac{b}{a}\mathrm{B} \rightarrow \frac{c}{a}\mathrm{C} + \frac{d}{a}\mathrm{D}" />
              <p style={{ marginTop: '10px', fontSize: '13px', color: 'var(--t5-text-muted)' }}>
                where A is the limiting reactant.
              </p>
            </div>

            <div className="cre5-equation-box cre5-equation-box--flow">
              <div className="cre5-eq-label">Key Definitions</div>
              <BlockMath math="\Theta_i = \frac{F_{i0}}{F_{A0}} = \frac{C_{i0}}{C_{A0}} = \frac{y_{i0}}{y_{A0}}" />
              <BlockMath math="\delta = \frac{d}{a} + \frac{c}{a} - \frac{b}{a} - 1" />
              <p style={{ marginTop: '10px', fontSize: '13px', color: 'var(--t5-text-muted)' }}>
                δ = change in total number of moles per mole of A reacted
              </p>
            </div>
          </section>

          {/* FLOW SYSTEM STOICHIOMETRY TABLE */}
          <section className="cre5-section" id="flow-system">
            <div className="cre5-section-label">Stoichiometric Tables</div>
            <h2>Flow System Stoichiometry Table</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-diagram-container">
              <FlowSystemStoichTable />
            </div>
          </section>

          {/* CONCENTRATION DEFINITIONS */}
          <section className="cre5-section" id="flow-definitions">
            <div className="cre5-section-label">Definitions</div>
            <h2>Concentration Definitions in Flow Systems</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-definitions-grid">
              <div className="cre5-def-card">
                <div className="cre5-def-title">Molar Concentration</div>
                <BlockMath math="C_A = \frac{F_A}{\nu}" />
                <p style={{ marginTop: '8px', fontSize: '12px', color: 'var(--t5-text-muted)' }}>
                  moles per unit volume
                </p>
              </div>

              <div className="cre5-def-card">
                <div className="cre5-def-title">Liquid Phase (ν = ν₀)</div>
                <BlockMath math="C_A = C_{A0}(1-X)" />
                <BlockMath math="C_B = C_{A0}\left(\Theta_B - \frac{b}{a}X\right)" />
              </div>

              <div className="cre5-def-card">
                <div className="cre5-def-title">Partial Pressure</div>
                <BlockMath math="P_A = C_A RT = y_A P_T" />
              </div>

              <div className="cre5-def-card">
                <div className="cre5-def-title">Mole Fraction</div>
                <BlockMath math="y_A = \frac{F_A}{F_T}" />
              </div>
            </div>
          </section>

          {/* GAS PHASE FLOW */}
          <section className="cre5-section" id="gas-phase-flow">
            <div className="cre5-section-label">Advanced Topic</div>
            <h2>Gas Phase Flow System</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-equation-box cre5-equation-box--gas">
              <div className="cre5-eq-label">Volumetric Flow Rate (Ideal Gas)</div>
              <BlockMath math="\nu = \nu_0 \frac{F_T}{F_{T0}} \frac{T}{T_0} \frac{P_0}{P}" />
              
              <div style={{ marginTop: '20px' }}>
                <strong>Substituting F<sub>T</sub>:</strong>
              </div>
              <BlockMath math="\nu = \nu_0 \left(1 + y_{A0}\delta X\right) \frac{T}{T_0} \frac{P_0}{P}" />
              
              <div style={{ marginTop: '20px' }}>
                <strong>Define ε = y<sub>A0</sub>δ:</strong>
              </div>
              <BlockMath math="\nu = \nu_0 \left(1 + \epsilon X\right) \frac{T}{T_0} \frac{P_0}{P}" />
              <p style={{ marginTop: '10px', fontSize: '13px', color: 'var(--t5-text-muted)' }}>
                ε = change in total number of moles for complete conversion / total number of moles fed
              </p>
            </div>

            <div className="cre5-equation-box">
              <div className="cre5-eq-label">Concentrations in Gas Phase Flow</div>
              <BlockMath math="C_A = \frac{C_{A0}(1-X)}{(1+\epsilon X)} \frac{T_0}{T} \frac{P}{P_0}" />
              <BlockMath math="C_B = \frac{C_{A0}\left(\Theta_B - \frac{b}{a}X\right)}{(1+\epsilon X)} \frac{T_0}{T} \frac{P}{P_0}" />
            </div>
          </section>

          {/* STOICHIOMETRIC TABLE REFERENCE */}
          <section className="cre5-section" id="stoich-table-flow">
            <div className="cre5-section-label">Reference</div>
            <h2>Complete Stoichiometric Table: Flow System</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-stoich-table-container">
              <table className="cre5-stoich-table">
                <thead>
                  <tr>
                    <th>Species</th>
                    <th>Feed Rate</th>
                    <th>Change</th>
                    <th>Outlet Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>A</strong></td>
                    <td><BlockMath math="F_{A0}" /></td>
                    <td><BlockMath math="-F_{A0}X" /></td>
                    <td><BlockMath math="F_{A0}(1-X)" /></td>
                  </tr>
                  <tr>
                    <td><strong>B</strong></td>
                    <td><BlockMath math="F_{B0} = \Theta_B F_{A0}" /></td>
                    <td><BlockMath math="-\frac{b}{a}F_{A0}X" /></td>
                    <td><BlockMath math="F_{A0}(\Theta_B - \frac{b}{a}X)" /></td>
                  </tr>
                  <tr>
                    <td><strong>C</strong></td>
                    <td><BlockMath math="F_{C0} = \Theta_C F_{A0}" /></td>
                    <td><BlockMath math="+\frac{c}{a}F_{A0}X" /></td>
                    <td><BlockMath math="F_{A0}(\Theta_C + \frac{c}{a}X)" /></td>
                  </tr>
                  <tr>
                    <td><strong>D</strong></td>
                    <td><BlockMath math="F_{D0} = \Theta_D F_{A0}" /></td>
                    <td><BlockMath math="+\frac{d}{a}F_{A0}X" /></td>
                    <td><BlockMath math="F_{A0}(\Theta_D + \frac{d}{a}X)" /></td>
                  </tr>
                  <tr>
                    <td><strong>Inert I</strong></td>
                    <td><BlockMath math="F_{I0} = \Theta_I F_{A0}" /></td>
                    <td>0</td>
                    <td><BlockMath math="F_{A0} \Theta_I" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* EQUILIBRIUM CONVERSION */}
          <section className="cre5-section" id="equilibrium-concept">
            <div className="cre5-lec-badge cre5-lec-badge--equilibrium">Equilibrium Concepts</div>
            <h2>Equilibrium Conversion (X<sub>e</sub>)</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-equation-box cre5-equation-box--primary">
              <div className="cre5-eq-label">Equilibrium Condition</div>
              <BlockMath math="-r_A = k_A\left[C_A^2 - \frac{C_B}{K_C}\right] = 0" />
              <BlockMath math="K_C = \frac{C_B}{C_A^2}" />
              <p style={{ marginTop: '15px', fontSize: '13px', color: 'var(--t5-text-muted)' }}>
                At equilibrium, the net rate of reaction is zero, and the system reaches its maximum theoretical conversion.
              </p>
            </div>

            <div className="cre5-callout cre5-callout--info">
              <strong>Key Insight:</strong> The equilibrium conversion depends on:
              <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                <li>Equilibrium constant K<sub>C</sub> (temperature dependent)</li>
                <li>Initial concentrations or feed ratios</li>
                <li>Reactor type (batch vs. flow reactors can achieve different equilibrium conversions)</li>
                <li>Total pressure (for gas phase reactions)</li>
              </ul>
            </div>
          </section>

          {/* FLOW REACTOR EXAMPLE */}
          <section className="cre5-section" id="flow-reactor">
            <div className="cre5-section-label">Example</div>
            <h2>Flow Reactor: Calculate X<sub>ef</sub></h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-example-box">
              <div className="cre5-example-header">
                <strong>Reaction:</strong> 2A ⇌ B
              </div>
              <div style={{ marginTop: '15px' }}>
                <strong>Given:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                  <li>K<sub>C</sub> = 20 dm³/mol</li>
                  <li>C<sub>A0</sub> = 0.2 mol/dm³</li>
                  <li>Pure A feed (y<sub>A0</sub> = 1)</li>
                  <li>Isothermal, isobaric operation (T = T₀, P = P₀)</li>
                </ul>
              </div>

              <div style={{ marginTop: '20px' }}>
                <strong>Stoichiometric Table (Flow):</strong>
              </div>
              <table className="cre5-table" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th>Species</th>
                    <th>Feed</th>
                    <th>Change</th>
                    <th>Outlet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>A</td>
                    <td>F<sub>A0</sub></td>
                    <td>-F<sub>A0</sub>X</td>
                    <td>F<sub>A0</sub>(1-X)</td>
                  </tr>
                  <tr>
                    <td>B</td>
                    <td>0</td>
                    <td>+F<sub>A0</sub>X/2</td>
                    <td>F<sub>A0</sub>X/2</td>
                  </tr>
                  <tr>
                    <td>Total</td>
                    <td>F<sub>T0</sub> = F<sub>A0</sub></td>
                    <td>-F<sub>A0</sub>X/2</td>
                    <td>F<sub>T</sub> = F<sub>A0</sub>(1 - X/2)</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: '20px' }}>
                <strong>ε = y<sub>A0</sub>δ = 1 × (½ - 1) = -½</strong>
              </div>

              <div style={{ marginTop: '20px' }}>
                <strong>Concentrations (Gas Phase Flow):</strong>
              </div>
              <BlockMath math="C_A = \frac{C_{A0}(1-X)}{(1+\epsilon X)} = \frac{C_{A0}(1-X)}{(1 - X/2)}" />
              <BlockMath math="C_B = \frac{C_{A0}X/2}{(1+\epsilon X)} = \frac{C_{A0}X/2}{(1 - X/2)}" />

              <div style={{ marginTop: '20px' }}>
                <strong>At Equilibrium: -r<sub>A</sub> = 0</strong>
              </div>
              <BlockMath math="K_C = \frac{C_B}{C_A^2} = \frac{\frac{C_{A0}X_e/2}{(1 - X_e/2)}}{\left[\frac{C_{A0}(1-X_e)}{(1 - X_e/2)}\right]^2} = \frac{X_e(1 - X_e/2)}{2C_{A0}(1-X_e)^2}" />

              <div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'var(--t5-accent-dim)', borderRadius: '6px' }}>
                <strong>Substitute values:</strong>
                <BlockMath math="2K_C C_{A0} = 2(20)(0.2) = 8 = \frac{X_e(1 - X_e/2)}{(1-X_e)^2}" />
                <BlockMath math="8 = \frac{X_e - 0.5X_e^2}{1 - 2X_e + X_e^2}" />
                <BlockMath math="8.5X_e^2 - 17X_e + 8 = 0" />
                <strong>Solution: X<sub>ef</sub> = 0.757 or 75.7%</strong>
              </div>
            </div>
          </section>

          {/* BATCH REACTOR EXAMPLE & COMPARISON */}
          <section className="cre5-section" id="batch-reactor">
            <div className="cre5-section-label">Comparison</div>
            <h2>Batch Reactor: Calculate X<sub>eb</sub></h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-equation-box cre5-equation-box--compare">
              <div className="cre5-eq-label">Batch System (Constant Volume)</div>
              <p style={{ marginTop: '10px', fontSize: '13px' }}>
                For the same reaction 2A ⇌ B with the same initial concentration and K<sub>C</sub>:
              </p>
              <BlockMath math="C_A = C_{A0}(1-X)" />
              <BlockMath math="C_B = \frac{C_{A0}X}{2}" />
              <BlockMath math="K_C = \frac{C_B}{C_A^2} = \frac{C_{A0}X/2}{[C_{A0}(1-X)]^2} = \frac{X}{2C_{A0}(1-X)^2}" />
              <BlockMath math="2K_C C_{A0} = 8 = \frac{X_e}{(1-X_e)^2}" />
              
              <div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'var(--t5-blue-dim)', borderRadius: '6px' }}>
                <strong>Batch Equilibrium Conversion:</strong> <strong>X<sub>eb</sub> = 0.703 or 70.3%</strong>
              </div>
            </div>

            {/* Equilibrium Comparison Diagram */}
            <div className="cre5-diagram-container">
              <EquilibriumConversionDiagram />
            </div>

            <div className="cre5-callout cre5-callout--key">
              <p><strong>Comparison:</strong> Flow reactor achieves higher equilibrium conversion (75.7% vs 70.3%) due to volume change effects from the gas phase reaction (ε = -0.5).</p>
            </div>
          </section>

          {/* KEY SYMBOLS */}
          <section className="cre5-section" id="key-symbols">
            <div className="cre5-section-label">Reference</div>
            <h2>Key Symbols &amp; Notation</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-symbols-table-container">
              <table className="cre5-symbols-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Definition</th>
                    <th>Units</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>X</td>
                    <td>Conversion of reactant A</td>
                    <td>dimensionless</td>
                  </tr>
                  <tr>
                    <td>X<sub>e</sub></td>
                    <td>Equilibrium conversion</td>
                    <td>dimensionless</td>
                  </tr>
                  <tr>
                    <td>F<sub>A</sub></td>
                    <td>Molar flow rate of A</td>
                    <td>mol/s or mol/min</td>
                  </tr>
                  <tr>
                    <td>F<sub>A0</sub></td>
                    <td>Inlet molar flow rate of A</td>
                    <td>mol/s or mol/min</td>
                  </tr>
                  <tr>
                    <td>C<sub>A</sub></td>
                    <td>Molar concentration of A</td>
                    <td>mol/dm³ or mol/L</td>
                  </tr>
                  <tr>
                    <td>C<sub>A0</sub></td>
                    <td>Inlet concentration of A</td>
                    <td>mol/dm³ or mol/L</td>
                  </tr>
                  <tr>
                    <td>K<sub>C</sub></td>
                    <td>Equilibrium constant (concentration basis)</td>
                    <td>(mol/dm³)^(Δn)</td>
                  </tr>
                  <tr>
                    <td>δ</td>
                    <td>Change in total moles per mole A reacted</td>
                    <td>dimensionless</td>
                  </tr>
                  <tr>
                    <td>ε</td>
                    <td>y<sub>A0</sub>δ — expansion factor</td>
                    <td>dimensionless</td>
                  </tr>
                  <tr>
                    <td>Θ<sub>i</sub></td>
                    <td>Initial molar ratio (F<sub>i0</sub>/F<sub>A0</sub>)</td>
                    <td>dimensionless</td>
                  </tr>
                  <tr>
                    <td>ν</td>
                    <td>Volumetric flow rate</td>
                    <td>dm³/s or L/s</td>
                  </tr>
                  <tr>
                    <td>−r<sub>A</sub></td>
                    <td>Rate of disappearance of A per unit volume</td>
                    <td>mol/dm³/s</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* REACTOR SUMMARY */}
          <section className="cre5-section" id="reactor-summary">
            <div className="cre5-section-label">Quick Reference</div>
            <h2>Reactor Type Summary</h2>
            <div className="cre5-section-divider"></div>

            <div className="cre5-reactor-cards">
              <div className="cre5-reactor-card cre5-reactor-card--batch">
                <div className="cre5-card-title">Batch Reactor</div>
                <div className="cre5-card-eq">
                  <strong>Design Equation:</strong>
                  <BlockMath math="t = N_{A0}\int_0^X \frac{dX}{-r_A V}" />
                </div>
                <div className="cre5-card-info">
                  <strong>Characteristics:</strong>
                  <ul>
                    <li>Time-dependent operation</li>
                    <li>Unsteady state</li>
                    <li>All material in reactor</li>
                  </ul>
                </div>
              </div>

              <div className="cre5-reactor-card cre5-reactor-card--cstr">
                <div className="cre5-card-title">CSTR</div>
                <div className="cre5-card-eq">
                  <strong>Design Equation:</strong>
                  <BlockMath math="V = \frac{F_{A0}X}{-r_A}" />
                </div>
                <div className="cre5-card-info">
                  <strong>Characteristics:</strong>
                  <ul>
                    <li>Steady state</li>
                    <li>Perfect mixing</li>
                    <li>Outlet = average</li>
                  </ul>
                </div>
              </div>

              <div className="cre5-reactor-card cre5-reactor-card--pfr">
                <div className="cre5-card-title">PFR</div>
                <div className="cre5-card-eq">
                  <strong>Design Equation:</strong>
                  <BlockMath math="V = F_{A0}\int_0^X \frac{dX}{-r_A}" />
                </div>
                <div className="cre5-card-info">
                  <strong>Characteristics:</strong>
                  <ul>
                    <li>Steady state</li>
                    <li>Plug flow</li>
                    <li>Concentration varies</li>
                  </ul>
                </div>
              </div>

              <div className="cre5-reactor-card cre5-reactor-card--pbr">
                <div className="cre5-card-title">PBR</div>
                <div className="cre5-card-eq">
                  <strong>Design Equation:</strong>
                  <BlockMath math="W = F_{A0}\int_0^X \frac{dX}{-r'_A}" />
                </div>
                <div className="cre5-card-info">
                  <strong>Characteristics:</strong>
                  <ul>
                    <li>Packed with catalyst</li>
                    <li>Accounts for aging</li>
                    <li>Continuous operation</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* FOOTER */}
          <div className="cre5-section-divider" style={{ marginTop: '60px' }}></div>
          <footer className="cre5-footer">
            <p>
              CRE Lecture 5 - Stoichiometry and Equilibrium Conversion - Chapter 4 - Fogler's Elements of CRE (6th Edition)
            </p>
          </footer>
        </div>
      </main>
    </div>

        </div>   {/* end scrollable content */}
      </div>     {/* end rightCol */}
    </div>       
  );
};

export default ChemicalReactionEngineeringNotes5;